import { createAsyncThunk, createSelector, createSlice } from '@reduxjs/toolkit';
import DdtService from 'services/DdtService';
import { getVatRatePercentage } from 'configs/vatRateConfig';
import { isDdtChiusoPerCicloPassivo } from 'utils/ddtStatus';

const initialState = {
  loadingList: false,
  loadedList: false,
  errorList: false,
  creating: false,
  errorCreate: false,
  ddt: [],
};

export const loadDdt = createAsyncThunk('ddt/load', async (companyDocumentId, { rejectWithValue }) => {
  try {

    const res = await DdtService.list(companyDocumentId);
    return res.data;
  } catch (e) {
    return rejectWithValue(e.message || 'Errore caricamento DDT');
  }
});

export const createDdt = createAsyncThunk('ddt/create', async (payload, { rejectWithValue }) => {
  try {
      const res = await DdtService.create(payload.body,payload.companyId);
      return res.order;
  } catch (e) {
    return rejectWithValue(e.status || 500);
  }
});

export const createMdsDdt = createAsyncThunk('ddt/mds/create', async (payload, { rejectWithValue }) => {
    try {
        const res = await DdtService.createMds(payload.body,payload.companyId);
        return res.order;
    } catch (e) {
        return rejectWithValue(e.status || 500);
    }
});

const ddtSlice = createSlice({
  name: 'ddt',
  initialState,
  reducers: {
    resetDdtState: () => initialState,

    /**
     * Aggiorna lo stato_passivo di un DDT dopo l'aggancio a una fattura passiva.
     * Uso: dispatch(updateDdtStato({ id: 'ddt-123', stato: 'fatturata' }))
     */
    updateDdtStato: (state, action) => {
      const { id, stato } = action.payload;
      const found = state.ddt.find((d) => d.id === id || d.documentId === id);
      if (found) {
        found.stato_passivo = stato;
      }
    },

    /**
     * Riporta un DDT a stato aperto (rettifica / annullamento aggancio).
     * Uso: dispatch(detachDdtDaFattura({ id: 'ddt-123' }))
     */
    detachDdtDaFattura: (state, action) => {
      const { id } = action.payload;
      const found = state.ddt.find((d) => d.id === id || d.documentId === id);
      if (found) {
        found.stato_passivo = null;
      }
    },
  },
  extraReducers: builder => {
    builder
      .addCase(loadDdt.pending, state => {
        state.loadingDdt = true;
        state.errorDdt = false;
      })
      .addCase(loadDdt.fulfilled, (state, action) => {
        state.loadingDdt = false;
        state.loadedDdt = true;
        state.errorDdt = false;
        state.ddt = Array.isArray(action.payload) ? action.payload : [];
      })
      .addCase(loadDdt.rejected, state => {
        state.loadingDdt = false;
        state.loadedDdt = true;
        state.errorDdt = true;
        state.ddt = [];
      })
      .addCase(createDdt.pending, state => {
        state.creatingDdt = true;
        state.errorCreate = false;
      })
      .addCase(createMdsDdt.pending, state => {
        state.creatingDdt = true;
        state.errorCreate = false;
      })
      .addCase(createDdt.fulfilled, (state, action) => {
        state.creatingDdt = false;
        state.errorCreateDdt = false;
        state.ddt = [action.payload, ...state.ddt];
      })
     .addCase(createMdsDdt.fulfilled, (state, action) => {
        state.creatingDdt = false;
        state.errorCreateDdt = false;
        state.ddt = [action.payload, ...state.ddt];
     })
     .addCase(createDdt.rejected, state => {
        state.creatingDdt = false;
        state.errorCreateDdt = true;
     })
     .addCase(createMdsDdt.rejected, state => {
        state.creatingDdt = false;
        state.errorCreateDdt = true;
     });
  }
});

export const { resetDdtState, updateDdtStato, detachDdtDaFattura } = ddtSlice.actions;
export default ddtSlice.reducer;

// ---------------------------------------------------------------------------
// Selectors
// ---------------------------------------------------------------------------

const selectDdtList = (state) => state.ddt.ddt;
const selectDdtLoading = (state) => state.ddt.loadingDdt;
const selectDdtLoaded = (state) => state.ddt.loadedDdt;

const toNumber = (value, fallback = 0) => {
    const normalized = typeof value === 'string' ? value.replace(',', '.') : value;
    const numeric = Number(normalized);
    return Number.isFinite(numeric) ? numeric : fallback;
};

const unwrapRelation = (value) => {
    if (!value) return null;
    if (value.data) return unwrapRelation(value.data);
    if (Array.isArray(value)) return value.map(unwrapRelation).filter(Boolean);
    if (value.attributes) {
        return {
            ...value.attributes,
            id: value.id ?? value.attributes.id,
            documentId: value.documentId ?? value.attributes.documentId,
        };
    }
    return value;
};

const firstRelationItem = (value) => {
    const unwrapped = unwrapRelation(value);
    return Array.isArray(unwrapped) ? unwrapped.find(Boolean) || null : unwrapped;
};

const getGroupPurchaseAccount = (group) => {
    const account = unwrapRelation(group?.conto_dare);
    return (
        account?.descrizione ||
        account?.description ||
        account?.name ||
        account?.codice ||
        account?.code ||
        group?.purchaseAccount ||
        null
    );
};

const getGroupSalesAccount = (group) => {
    const account = unwrapRelation(group?.conto_avere);
    return (
        group?.salesAccount ||
        account?.descrizione ||
        account?.description ||
        account?.name ||
        account?.codice ||
        account?.code ||
        null
    );
};

const normalizeLookupKey = (value) =>
    String(value || '')
        .trim()
        .toLowerCase();

const getNonSerialValue = (value, lot = {}) => {
    const normalized = normalizeLookupKey(value);
    if (!normalized) return null;

    const serialValues = [
        lot.serial_number,
        lot.serial,
        lot.lot_code,
        lot.id,
        lot.documentId,
    ]
        .map(normalizeLookupKey)
        .filter(Boolean);

    return serialValues.includes(normalized) ? null : value;
};

const buildLookup = (items = [], getKeys) => {
    const lookup = {};
    items.forEach((item) => {
        getKeys(item)
            .map(normalizeLookupKey)
            .filter(Boolean)
            .forEach((key) => {
                lookup[key] = item;
            });
    });
    return lookup;
};

const getProductKeys = (product = {}) => [
    product.documentId,
    product.id,
    product.id_maffle,
    product.code,
    product.name,
    product.product?.documentId,
    product.product?.id,
    product.product?.id_maffle,
    product.product?.code,
    product.product?.name,
    ...(product.warehouse_articles || []).flatMap((warehouseArticle) => {
        const lot = unwrapRelation(warehouseArticle?.lot) || {};
        return [
            lot.documentId,
            lot.id,
            lot.serial_number,
            lot.serial,
        ];
    }),
];

const getStableProductIdentity = (product = {}, lot = {}) =>
    [
        product.documentId,
        product.id_maffle,
        product.code,
        product.id,
        getNonSerialValue(product.name, lot),
        lot.product_code,
    ]
        .map((value) => String(value || '').trim())
        .find(Boolean) || null;

const getGroupKeys = (group = {}) => [
    group.documentId,
    group.id,
    group.nome,
    group.name,
    group.label,
    group.merchandise_group?.documentId,
    group.merchandise_group?.id,
    group.merchandise_group?.nome,
];

const resolveFromLookup = (value, lookup = {}, getKeys = () => []) => {
    const unwrapped = unwrapRelation(value);

    if (!unwrapped) {
        return null;
    }

    if (typeof unwrapped !== 'object') {
        return lookup[normalizeLookupKey(unwrapped)] || null;
    }

    return (
        getKeys(unwrapped)
            .map((key) => lookup[normalizeLookupKey(key)])
            .find(Boolean) ||
        unwrapped
    );
};

/** Vero se i DDT sono stati caricati (sia in caso di successo che di errore) */
export const selectDdtLoadedForCompany = (state) => Boolean(state.ddt.loadedDdt);

const mapDdtForPassive = (ddt, productLookup = {}, groupLookup = {}) => {
    const supplier = firstRelationItem(ddt.supplier) || {};
    const company = firstRelationItem(ddt.company) || {};
    const lots = unwrapRelation(ddt.lots) || [];

    return {
        id: ddt.id,
        documentId: ddt.documentId,
        supplierId: supplier.documentId || supplier.id,
        supplierName: supplier.company_name || supplier.supplier_code || '–',
        supplierVat: supplier.vat_number || supplier.piva || null,
        supplierDocumentNumber: ddt.referenceCode,
        internalNumber: ddt.referenceCode,
        companyDocumentId: company.documentId || null,
        status: ddt.stato_passivo || (ddt.state === 'OPEN' ? 'aperta' : ddt.state?.toLowerCase() || 'aperta'),
        receivedAt: ddt.date || ddt.creation_date || null,
        lines: (Array.isArray(lots) ? lots : []).map((lot) => {
            const warehouseArticle = firstRelationItem(lot.warehouse_articles || lot.warehouse_article) || {};
            const rawLotProduct = firstRelationItem(lot.product) || {};
            const rawWarehouseProduct = firstRelationItem(warehouseArticle.product) || {};
            const rawDdtProduct = rawLotProduct.documentId || rawLotProduct.id ? rawLotProduct : rawWarehouseProduct;
            const lookupProduct =
                resolveFromLookup(lot.product, productLookup, getProductKeys) ||
                resolveFromLookup(warehouseArticle.product, productLookup, getProductKeys) ||
                resolveFromLookup(lot.serial_number || lot.serial, productLookup, getProductKeys) ||
                resolveFromLookup(rawDdtProduct?.documentId, productLookup, getProductKeys) ||
                resolveFromLookup(rawDdtProduct?.id, productLookup, getProductKeys) ||
                null;
            const ddtProduct = lookupProduct || rawDdtProduct;
            const catalogProduct =
                getProductKeys({ ...rawDdtProduct, ...ddtProduct })
                    .map((key) => productLookup[normalizeLookupKey(key)])
                    .find(Boolean) || {};
            const productFromCatalog = unwrapRelation(catalogProduct.product) || {};
            const product = {
                ...catalogProduct,
                ...productFromCatalog,
                ...ddtProduct,
                merchandise_group:
                    rawLotProduct.merchandise_group ||
                    rawWarehouseProduct.merchandise_group ||
                    ddtProduct.merchandise_group ||
                    productFromCatalog.merchandise_group ||
                    catalogProduct.merchandise_group,
            };
            const productDisplayName =
                getNonSerialValue(productFromCatalog.name, lot) ||
                getNonSerialValue(catalogProduct.name, lot) ||
                getNonSerialValue(rawLotProduct.name, lot) ||
                getNonSerialValue(rawWarehouseProduct.name, lot) ||
                getNonSerialValue(ddtProduct.name, lot) ||
                getNonSerialValue(product.name, lot) ||
                null;
            const productIdentity = getStableProductIdentity(product, lot);
            const productGroup = resolveFromLookup(
                product.merchandise_group ||
                product.merchandiseGroup ||
                product.group ||
                lot.merchandise_group ||
                lot.merchandiseGroup,
                groupLookup,
                getGroupKeys
            ) || {};
            const merchandiseGroup =
                getGroupKeys(productGroup)
                    .map((key) => groupLookup[normalizeLookupKey(key)])
                    .find(Boolean) ||
                productGroup;
            const lotType = lot.type || product.type || null;
            const serialized = lotType === 'serialized';
            const quantity = serialized
                ? 1
                : toNumber(
                    lot.quantity ??
                    warehouseArticle.quantity ??
                    lot.warehouse_article?.quantity,
                    1
                ) || 1;

            return {
                id: lot.id || lot.documentId,
                productId: productIdentity,
                productIdentity,
                productDocumentId: product.documentId || null,
                productCode: product.id_maffle || product.code || lot.product_code || null,
                lotId: lot.id || null,
                lotDocumentId: lot.documentId || null,
                articleName: productDisplayName,
                articleCode:
                    product.id_maffle ||
                    product.code ||
                    lot.product_code ||
                    product.documentId ||
                    product.id ||
                    null,
                description:
                    productDisplayName ||
                    getNonSerialValue(lot.description, lot) ||
                    'Articolo non risolto',
                productName: productDisplayName,
                quantity,
                unitPrice: toNumber(
                    lot.unit_cost ??
                    lot.unit_price ??
                    lot.purchase_price ??
                    product.price,
                    0
                ),
                discount: toNumber(lot.discount, 0),
                vatRate: getVatRatePercentage(lot.vat_rate ?? product.vat_rate),
                serialNumber: lot.serial_number || lot.serial || null,
                lotCode: lot.lot_code || null,
                groupId: merchandiseGroup.documentId || merchandiseGroup.id || null,
                groupDocumentId: merchandiseGroup.documentId || null,
                groupStrapiId: merchandiseGroup.id || null,
                groupLabel: merchandiseGroup.nome || merchandiseGroup.name || merchandiseGroup.label || null,
                purchaseAccount: getGroupPurchaseAccount(merchandiseGroup),
                salesAccount: getGroupSalesAccount(merchandiseGroup),
                isService: lotType === 'service',
            };
        }),
    };
};

export const selectDdtPerCicloPassivo = createSelector(
    [
        selectDdtList,
        (state) => state.prodotti?.prodotti || [],
        (state) => state.gruppiMerceologici?.gruppi || [],
        (_state, supplierDocumentId) => supplierDocumentId,
    ],
    (ddtList, products, groups, supplierDocumentId) => {
        const productLookup = buildLookup(products, getProductKeys);
        const groupLookup = buildLookup(groups, getGroupKeys);
        const filtered = supplierDocumentId
            ? ddtList.filter(
                  (ddt) => {
                      const supplier = firstRelationItem(ddt.supplier) || {};
                      return (supplier.documentId || supplier.id) === supplierDocumentId;
                  }
              )
            : ddtList;

        return filtered.filter((ddt) => ddt.type !== 'SUPPLIER_RETURN').map((ddt) => mapDdtForPassive(ddt, productLookup, groupLookup));
    }
);

/**
 * Ritorna i DDT disponibili per il ciclo passivo (stati aperti/da fatturare).
 * Mappa i campi del backend verso la struttura attesa dal pannello di selezione DDT.
 *
 * @param {string|null} supplierDocumentId - documentId del fornitore per filtrare (opzionale)
 */
export const selectDdtApertiPerCicloPassivo = createSelector(
    [selectDdtPerCicloPassivo, selectDdtLoading, selectDdtLoaded],
    (ddtList, _loading, _loaded) => {
        return ddtList.filter((ddt) => !isDdtChiusoPerCicloPassivo(ddt.status));
    }
);
